// api.js — All API calls to the Hono + Node backend at /api/*.
// In dev, Vite proxies /api/* to localhost:3000. In prod, Caddy does it.

let token = localStorage.getItem("cg_token") || null;

export function getToken() { return token; }
export function setToken(t) { token = t; if (t) localStorage.setItem("cg_token", t); else localStorage.removeItem("cg_token"); }

async function request(path, options = {}) {
  const headers = { "Content-Type": "application/json", ...options.headers };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`/api${path}`, { ...options, headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

// ─── Auth ───
export const api = {
  register: (email, displayName, password, altcha, referralCode, consent = {}, extra = {}) =>
    request("/auth/register", { method: "POST", body: JSON.stringify({
      email, displayName, password, altcha, referralCode,
      acceptedTerms: !!consent.acceptedTerms,
      phone:    extra.phone || undefined,
      nickname: extra.nickname || undefined,
      // Language picked on the auth screen — saved to user.language so
      // the first authed session resolves to the same language they
      // registered in. Accepted values: 'en', 'ru', 'es'.
      language: extra.language || undefined,
    }) }),

  // Live nickname availability check for the sign-up form. Returns
  // { available: boolean, reason?: "invalid" | "reserved" | "taken" }.
  checkNickname: (value) => {
    const v = String(value || "").trim().toLowerCase();
    return request(`/auth/check-nickname?value=${encodeURIComponent(v)}`);
  },

  verify: (email, code) =>
    request("/auth/verify", { method: "POST", body: JSON.stringify({ email, code }) }),

  login: (email, password, altcha) =>
    request("/auth/login", { method: "POST", body: JSON.stringify({ email, password, altcha }) }),

  me: () => request("/me"),
  updateMe: (patch) => request("/me", { method: "PATCH", body: JSON.stringify(patch) }),
  changePassword: (currentPassword, newPassword) =>
    request("/me/password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }),

  // ─── Reseller application gate (migration 0052) ───
  // Self: read current state (includes verification code) + submit proof.
  // Admin: queue listing + decide.
  getApplication: () => request("/me/application"),
  submitApplication: (payload) =>
    request("/me/application", { method: "POST", body: JSON.stringify(payload) }),
  adminListApplications: (status = "submitted") =>
    request(`/admin/applications?status=${encodeURIComponent(status)}`),
  adminApplicationCounts: () => request("/admin/applications/counts"),
  adminDecideApplication: (userId, decision, note) =>
    request(`/admin/applications/${userId}/decide`, {
      method: "POST",
      body: JSON.stringify({ decision, note }),
    }),

  // ─── Curated purchases (migration 0053) ───
  // Reseller-side
  listCurated: () => request("/me/curated"),
  getCurated: (id) => request(`/me/curated/${id}`),
  submitCurated: (payload) =>
    request("/me/curated", { method: "POST", body: JSON.stringify(payload) }),
  cancelCurated: (id) =>
    request(`/me/curated/${id}/cancel`, { method: "POST" }),
  curatedSettingsPublic: () => request("/me/curated/settings"),

  // Staff-side
  adminListCurated: (status) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return request(`/admin/curated${qs}`);
  },
  adminGetCurated: (id) => request(`/admin/curated/${id}`),
  adminRevealCuratedCredentials: (id) => request(`/admin/curated/${id}/credentials`),
  adminPriceCurated: (id, finalTotalCents, managerNotes) =>
    request(`/admin/curated/${id}/price`, {
      method: "POST",
      body: JSON.stringify({ finalTotalCents, managerNotes }),
    }),
  adminPurchasedCurated: (id, receiptImagePath) =>
    request(`/admin/curated/${id}/purchased`, {
      method: "POST",
      body: JSON.stringify({ receiptImagePath }),
    }),
  adminInvoiceCurated: (id, paymentTarget, currencyOverride) =>
    request(`/admin/curated/${id}/invoice`, {
      method: "POST",
      body: JSON.stringify({ paymentTarget, currencyOverride }),
    }),
  adminMarkPaidCurated: (id) =>
    request(`/admin/curated/${id}/mark-paid`, { method: "POST" }),
  adminCompleteCurated: (id) =>
    request(`/admin/curated/${id}/complete`, { method: "POST" }),
  adminRejectCurated: (id, reason) =>
    request(`/admin/curated/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  adminCuratedSettings: () => request("/admin/curated/settings"),
  adminUpdateCuratedSettings: (patch) =>
    request("/admin/curated/settings", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // ─── Users (admin/manager only — customers get 403) ───
  getUsers: () => request("/users"),
  setRole: (userId, role) =>
    request(`/users/${userId}/role`, { method: "POST", body: JSON.stringify({ role }) }),
  banUser: (userId, reason) =>
    request(`/users/${userId}/ban`, { method: "POST", body: JSON.stringify({ reason }) }),
  unbanUser: (userId) =>
    request(`/users/${userId}/unban`, { method: "POST" }),

  // ─── Managers (safe for everyone — used by customer DM picker) ───
  getManagers: () => request("/managers"),

  // ─── Groups ───
  getGroups: () => request("/groups"),
  createGroup: (name, description) =>
    request("/groups", { method: "POST", body: JSON.stringify({ name, description }) }),
  updateGroup: (groupId, patch) =>
    request(`/groups/${groupId}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteGroup: (groupId) =>
    request(`/groups/${groupId}`, { method: "DELETE" }),
  joinGroup: (groupId) =>
    request(`/groups/${groupId}/join`, { method: "POST" }),
  leaveGroup: (groupId) =>
    request(`/groups/${groupId}/leave`, { method: "POST" }),

  // ─── Topics ───
  getTopics: (groupId) => request(`/groups/${groupId}/topics`),
  createTopic: (groupId, name) =>
    request(`/groups/${groupId}/topics`, { method: "POST", body: JSON.stringify({ name }) }),
  updateTopic: (groupId, topicId, name) =>
    request(`/groups/${groupId}/topics/${topicId}`, { method: "PATCH", body: JSON.stringify({ name }) }),
  deleteTopic: (groupId, topicId) =>
    request(`/groups/${groupId}/topics/${topicId}`, { method: "DELETE" }),

  // ─── DMs ───
  getDms: () => request("/dms"),
  createDm: (targetUserId) =>
    request("/dms", { method: "POST", body: JSON.stringify({ targetUserId }) }),
  markDmRead: (id) => request(`/dms/${id}/read`, { method: "POST" }),

  // ─── Messages ───
  getMessages: (channelKey) => request(`/messages/${encodeURIComponent(channelKey)}`),
  searchMessages: (channelKey, q) =>
    request(`/messages/${encodeURIComponent(channelKey)}/search?q=${encodeURIComponent(q)}`),
  sendMessage: (channelKey, text, attachmentPath, replyToId) =>
    request(`/messages/${encodeURIComponent(channelKey)}`, {
      method: "POST",
      body: JSON.stringify({ text, attachmentPath, replyToId }),
    }),
  // Telegram-style message actions (migration 0078).
  reactToMessage: (messageId, emoji) =>
    request(`/messages/${messageId}/reaction`, { method: "POST", body: JSON.stringify({ emoji }) }),
  pinMessage: (messageId, pinned) =>
    request(`/messages/${messageId}/pin`, { method: "POST", body: JSON.stringify({ pinned }) }),
  // Admin-only message moderation (Phase Z-2)
  editMessage: (id, text) =>
    request(`/admin/messages/${id}`, { method: "PATCH", body: JSON.stringify({ text }) }),
  deleteMessage: (id) =>
    request(`/admin/messages/${id}`, { method: "DELETE" }),

  // ─── Channels (Phase 2) ───
  getChannels: () => request("/channels"),
  getChannel: (slug) => request(`/channels/${slug}`),
  createChannel: (data) => request("/channels", { method: "POST", body: JSON.stringify(data) }),
  updateChannel: (id, data) => request(`/channels/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  // Bulk-set commission/cashback for all channels of one store (source_retailer).
  setStoreFees: (sourceRetailer, patch) =>
    request("/channels/by-store/fees", {
      method: "PATCH",
      body: JSON.stringify({ sourceRetailer, ...patch }),
    }),
  deleteChannel: (id) => request(`/channels/${id}`, { method: "DELETE" }),
  setChannelSubscription: (id, prefs) =>
    request(`/channels/${id}/subscription`, { method: "POST", body: JSON.stringify(prefs) }),

  // ─── Posts (Phase 2) ───
  getPosts: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/posts${q ? "?" + q : ""}`);
  },
  getPost: (id) => request(`/posts/${id}`),
  createPost: (data) => request("/posts", { method: "POST", body: JSON.stringify(data) }),
  // Staff-only: generate a 2-sentence Russian description from typed
  // brand/title/notes. Used by the warehouse quick-add page so the
  // operator doesn't have to hand-write copy from the warehouse floor.
  // Channel-slug picks the right register (spec vs editorial).
  generateQuickDescription: (data) =>
    request("/ai/quick-description", { method: "POST", body: JSON.stringify(data) }),
  patchPost: (id, data) => request(`/posts/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  publishPost: (id) => request(`/posts/${id}/publish`, { method: "POST" }),
  orderIntent: (postId) => request(`/posts/${postId}/order-intent`, { method: "POST" }),
  rejectPost: (id, reason) =>
    request(`/posts/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) }),
  archivePost: (id) => request(`/posts/${id}/archive`, { method: "POST" }),
  deletePost: (id) => request(`/posts/${id}`, { method: "DELETE" }),
  // Group-buy (Feature 2)
  lockPost: (id) => request(`/posts/${id}/lock`, { method: "POST" }),
  confirmPendingOrders: (id) => request(`/posts/${id}/confirm-pending`, { method: "POST" }),

  // ─── Cashback (Phase 4a) ───
  getCashbackMerchants: () => request("/cashback/merchants"),
  createCashbackLink: (targetUrl, postId) =>
    request("/cashback/link", { method: "POST", body: JSON.stringify({ targetUrl, postId }) }),
  getCashbackBalance: () => request("/cashback/balance"),
  getCashbackTransactions: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/cashback/transactions${q ? "?" + q : ""}`);
  },
  createRedemption: (amountCents, method, deliveryDetails) =>
    request("/cashback/redemptions", { method: "POST", body: JSON.stringify({ amountCents, method, deliveryDetails }) }),
  getMyRedemptions: () => request("/cashback/redemptions"),
  getRedemptionQueue: (status = "pending") =>
    request(`/cashback/admin/redemptions?status=${encodeURIComponent(status)}`),
  fulfillRedemption: (id, deliveryDetails) =>
    request(`/cashback/admin/redemptions/${id}/fulfill`, { method: "POST", body: JSON.stringify({ deliveryDetails }) }),
  cancelRedemption: (id, reason) =>
    request(`/cashback/admin/redemptions/${id}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }),

  // ─── Audit log (admin only) ───
  getAuditLog: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/admin/audit${q ? "?" + q : ""}`);
  },

  // ─── Orders (Phase 1A — personal-shopper pivot) ───
  // opts: { variant?, qty, notes?, recipientName, recipientPhone?,
  //         shipAddress, shipCountry? }
  placeOrder: (postId, opts) =>
    request("/orders", { method: "POST", body: JSON.stringify({ postId, ...opts }) }),
  // Cart checkout: one order_number/order_group across N line items.
  // body: { items:[{postId, qty, variant?}], recipientName, recipientPhone?,
  //         shipAddress, shipCountry?, resellerCustomerId?, facilitated?, notes? }
  // Returns { orderNumber, orderGroupId, count, ids, skipped }.
  placeCartOrder: (body) =>
    request("/orders/cart", { method: "POST", body: JSON.stringify(body) }),
  // Reseller's own ruble bookkeeping price (kopecks; null to clear).
  setOrderResellerPrice: (id, resellerPriceRubCents) =>
    request(`/orders/${id}/reseller-price`, {
      method: "PATCH",
      body: JSON.stringify({ resellerPriceRubCents }),
    }),
  // Ask the seller for a payment QR/link in the personal DM. Returns { dmId }.
  requestPaymentLink: (id) =>
    request(`/orders/${id}/payment-request`, { method: "POST" }),
  // Per-order weight (kg; null to clear) for the batch delivery split.
  setOrderWeight: (id, weightKg) =>
    request(`/orders/${id}/weight`, {
      method: "PATCH",
      body: JSON.stringify({ weightKg }),
    }),

  // ─── Purchase batches (Закупка) ───
  listBatches: (scope = "self", status) => {
    const q = new URLSearchParams({ scope, ...(status ? { status } : {}) }).toString();
    return request(`/batches?${q}`);
  },
  getBatch: (id) => request(`/batches/${id}`),
  createBatch: (body) =>
    request("/batches", { method: "POST", body: JSON.stringify(body) }),
  updateBatch: (id, patch) =>
    request(`/batches/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  addBatchOrders: (id, orderIds) =>
    request(`/batches/${id}/orders`, { method: "POST", body: JSON.stringify({ orderIds }) }),
  removeBatchOrder: (id, orderId) =>
    request(`/batches/${id}/orders/${orderId}`, { method: "DELETE" }),
  deleteBatch: (id) => request(`/batches/${id}`, { method: "DELETE" }),
  getMyOrders: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/orders${q ? "?" + q : ""}`);
  },
  getOrder: (id) => request(`/orders/${id}`),
  // Admin
  getAdminOrders: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/admin/orders${q ? "?" + q : ""}`);
  },
  // status: pipeline value; cancelReason: optional; qtyFulfilled:
  // optional, only meaningful when status === 'purchased'. If 0, server
  // flips the transition to 'unavailable' instead.
  updateOrderStatus: (id, status, cancelReason, qtyFulfilled) =>
    request(`/admin/orders/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status, cancelReason, qtyFulfilled }),
    }),
  // Receipt photos at US warehouse (Feature 4). Appends to existing.
  addReceivedPhotos: (id, paths) =>
    request(`/admin/orders/${id}/received-photos`, {
      method: "POST",
      body: JSON.stringify({ paths }),
    }),

  // ─── Admin settings: FX rate (Phase 2) ───
  // GET returns { base, markupPct, effective, auto: { enabled, lastRunAt,
  // lastStatus, lastError, lastSourceRate, lastChangedAt } } — `auto` is
  // the state of the hourly CBR auto-updater.
  getFxSettings: () => request("/admin/settings/fx"),
  setFxSettings: (patch) =>
    request("/admin/settings/fx", { method: "PATCH", body: JSON.stringify(patch) }),
  // Manually trigger a fresh CBR fetch. Returns the same shape as
  // getFxSettings(); inspect `auto.lastStatus` for "ok" | "fail".
  refreshFxFromCbr: () =>
    request("/admin/settings/fx/refresh", { method: "POST", body: "{}" }),
  // Turn the hourly auto-updater on/off without touching base/markupPct.
  setFxAutoEnabled: (enabled) =>
    request("/admin/settings/fx/auto", {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    }),

  // ─── Payment confirmation (Feature 1) ───
  // Any authed user can read the operator's payment destinations.
  getPaymentMethods: () => request("/orders/_payment-methods"),
  // Customer uploads a screenshot for a confirmed order. Method must be
  // one of usdt_trc20, sbp, card.
  uploadPaymentProof: (orderId, screenshotPath, methodChosen) =>
    request(`/orders/${orderId}/payment-proof`, {
      method: "POST",
      body: JSON.stringify({ screenshotPath, methodChosen }),
    }),
  // Declared value per order (Feature 3). Pass null to clear and revert
  // to purchase price.
  setOrderDeclaredValue: (orderId, declaredValueUsdCents) =>
    request(`/orders/${orderId}/declared-value`, {
      method: "PATCH",
      body: JSON.stringify({ declaredValueUsdCents }),
    }),
  // Admin reads/writes operator payment destinations.
  getAdminPaymentMethods: () => request("/admin/settings/payment-methods"),
  setAdminPaymentMethods: (patch) =>
    request("/admin/settings/payment-methods", { method: "PATCH", body: JSON.stringify(patch) }),

  // ─── Shipments (Phase 3 — customer consolidation) ───
  getMyShipments: () => request("/shipments"),
  getShipment: (id) => request(`/shipments/${id}`),
  createShipment: (orderIds) =>
    request("/shipments", { method: "POST", body: JSON.stringify({ orderIds }) }),
  updateShipment: (id, patch) =>
    request(`/shipments/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  markShipmentReady: (id) =>
    request(`/shipments/${id}/ready`, { method: "POST" }),
  cancelShipment: (id) =>
    request(`/shipments/${id}`, { method: "DELETE" }),
  // Admin
  getAdminShipments: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/admin/shipments${q ? "?" + q : ""}`);
  },
  updateAdminShipment: (id, patch) =>
    request(`/admin/shipments/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  markShipmentShipped: (id) =>
    request(`/admin/shipments/${id}/shipped`, { method: "POST" }),
  markShipmentDelivered: (id) =>
    request(`/admin/shipments/${id}/delivered`, { method: "POST" }),

  // ─── Service tokens (Phase 5; admin only) ───
  getServiceTokens: () => request("/admin/tokens"),
  createServiceToken: (name, scopes) =>
    request("/admin/tokens", { method: "POST", body: JSON.stringify({ name, scopes }) }),
  revokeServiceToken: (id) =>
    request(`/admin/tokens/${id}/revoke`, { method: "POST" }),

  // ─── WhatsApp sender routes (admin only) ───
  getWaRoutes: () => request("/admin/whatsapp/routes"),
  getWaRecentSenders: () => request("/admin/whatsapp/routes/senders/recent"),
  upsertWaRoute: (data) => request("/admin/whatsapp/routes", { method: "POST", body: JSON.stringify(data) }),
  updateWaRoute: (id, data) => request(`/admin/whatsapp/routes/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteWaRoute: (id) => request(`/admin/whatsapp/routes/${id}`, { method: "DELETE" }),

  // ─── DM templates (Feature 6 — manager+) ───
  getDmTemplates: () => request("/dm-templates"),
  createDmTemplate: (data) =>
    request("/dm-templates", { method: "POST", body: JSON.stringify(data) }),
  updateDmTemplate: (id, data) =>
    request(`/dm-templates/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteDmTemplate: (id) =>
    request(`/dm-templates/${id}`, { method: "DELETE" }),

  // ─── Telegram link (Phase 6b) ───
  getTelegramLink: () => request("/me/telegram-link"),
  createTelegramLinkCode: () => request("/me/telegram-link", { method: "POST" }),
  unlinkTelegram: () => request("/me/telegram-link", { method: "DELETE" }),

  // ─── Reseller settings (Phase 7) ───
  // Per-user forward config: $→₽ rate, caption template, target chats.
  // Lazily created on first GET so the UI never sees a 404.
  getResellerSettings: () => request("/me/reseller-settings"),
  patchResellerSettings: (body) =>
    request("/me/reseller-settings", { method: "PATCH", body: JSON.stringify(body) }),

  // ─── Forwards (Phase 7) ───
  // Send a batch of posts to a set of configured targets. The API
  // expands the caption template + FX rate per post and pushes to the
  // Telegram Bot API synchronously, returning per-post per-target
  // results in the response body.
  createForward: ({ postIds, targetIds }) =>
    request("/forwards", { method: "POST", body: JSON.stringify({ postIds, targetIds }) }),
  listForwards: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/forwards${q ? "?" + q : ""}`);
  },
  getForward: (id) => request(`/forwards/${id}`),
  // Reseller analytics: { totals: {week, month, allTime}, topPosts, topTargets }
  // Server-aggregated over forwards.results JSONB. See routes/forwards.ts.
  getForwardStats: () => request("/forwards/stats"),

  // ─── Pulse — platform-analytics add-on (migration 0043) ───
  // Search log is fire-and-forget for any authed user. The five
  // read endpoints are gated server-side on users.pulse_until.
  logSearch: ({ query, channelSlug, matchedCount } = {}) =>
    request("/pulse/search-log", {
      method: "POST",
      body: JSON.stringify({ query, channelSlug, matchedCount }),
    }).catch(() => null),  // best-effort, never propagate
  getPulseTrending: () => request("/pulse/trending"),
  getPulseHotBrands: () => request("/pulse/hot-brands"),
  getPulseDiscountBuckets: () => request("/pulse/discount-buckets"),
  getPulseMissing: () => request("/pulse/missing"),
  getPulseDemandGaps: () => request("/pulse/demand-gaps"),

  // ─── Reseller customers (CRM, migration 0044) ───
  // Each reseller manages their downstream client list — name,
  // telegram handle, notes, etc. Scoped per reseller server-side.
  listMyCustomers: ({ q = "", limit = 100, offset = 0 } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (limit !== 100) params.set("limit", String(limit));
    if (offset) params.set("offset", String(offset));
    const qs = params.toString();
    return request(`/me/customers${qs ? "?" + qs : ""}`);
  },
  createMyCustomer: (body) => request("/me/customers", {
    method: "POST", body: JSON.stringify(body),
  }),
  updateMyCustomer: (id, body) => request(`/me/customers/${id}`, {
    method: "PATCH", body: JSON.stringify(body),
  }),
  deleteMyCustomer: (id) => request(`/me/customers/${id}`, {
    method: "DELETE",
  }),
  // Order history for a single customer (migration 0045).
  listMyCustomerOrders: (id) => request(`/me/customers/${id}/orders`),

  // ─── Saved addresses (migration 0054) ─────────────────────────
  // Two parallel books: one for the reseller's own addresses (used
  // when ordering for themselves), one tied to a specific customer
  // (used when ordering on behalf of someone in the CRM).
  listMyAddresses: () => request("/me/addresses"),
  createMyAddress: (body) => request("/me/addresses", {
    method: "POST", body: JSON.stringify(body),
  }),
  updateMyAddress: (id, body) => request(`/me/addresses/${id}`, {
    method: "PATCH", body: JSON.stringify(body),
  }),
  deleteMyAddress: (id) => request(`/me/addresses/${id}`, {
    method: "DELETE",
  }),
  listCustomerAddresses: (customerId) =>
    request(`/me/customers/${customerId}/addresses`),
  createCustomerAddress: (customerId, body) =>
    request(`/me/customers/${customerId}/addresses`, {
      method: "POST", body: JSON.stringify(body),
    }),
  updateCustomerAddress: (customerId, id, body) =>
    request(`/me/customers/${customerId}/addresses/${id}`, {
      method: "PATCH", body: JSON.stringify(body),
    }),
  deleteCustomerAddress: (customerId, id) =>
    request(`/me/customers/${customerId}/addresses/${id}`, {
      method: "DELETE",
    }),

  // ─── Reseller home dashboard ───
  // Returns { kpi: {forwards7d, deliveries7d, orders7d},
  //           pendingOrders: [...], recentCustomerActivity: [...] }
  // Single round-trip so the empty-state landing renders without a
  // waterfall.
  getMyDashboard: () => request(`/me/dashboard`),

  // ─── Per-customer follow-up reminders (migration 0046) ──────────
  // List reminders. mode="open" skips the completed history.
  listMyReminders: ({ mode = "all" } = {}) =>
    request(`/me/reminders?mode=${encodeURIComponent(mode)}`),
  listCustomerReminders: (customerId) =>
    request(`/me/reminders/by-customer/${customerId}`),
  createCustomerReminder: (customerId, { remindAt, note = "" }) =>
    request(`/me/reminders/by-customer/${customerId}`, {
      method: "POST",
      body: JSON.stringify({ remindAt, note }),
    }),
  updateReminder: (id, patch) =>
    request(`/me/reminders/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  deleteReminder: (id) =>
    request(`/me/reminders/${id}`, { method: "DELETE" }),

  // ─── Customer-bot invite (migration 0047) ────────────────────────
  // Per-customer t.me deeplink the reseller shares; the bot POSTs
  // back to the API on /start to insert the linkage. After that,
  // bulk messaging can route through the bot instead of falling back
  // to manual t.me/handle deeplinks.
  getCustomerInviteLink: (customerId) =>
    request(`/me/customers/${customerId}/invite-link`),
  getCustomerBotLink: (customerId) =>
    request(`/me/customers/${customerId}/bot-link`),
  // Bulk-broadcast through the customer-bot. Returns per-id results
  // so the caller can show "sent / no_link / failed".
  bulkMessageCustomers: ({ customerIds, template }) =>
    request(`/me/customers/bulk-message`, {
      method: "POST",
      body: JSON.stringify({ customerIds, template }),
    }),

  // ─── Notifications (migration 0048) ─────────────────────────────
  listNotifications: ({ cursor, limit = 30 } = {}) => {
    const q = new URLSearchParams();
    if (cursor) q.set("cursor", cursor);
    if (limit) q.set("limit", String(limit));
    const qs = q.toString();
    return request(`/me/notifications${qs ? "?" + qs : ""}`);
  },
  getUnreadNotificationCount: () =>
    request(`/me/notifications/unread-count`),
  markNotificationsRead: ({ ids, all } = {}) =>
    request(`/me/notifications/mark-read`, {
      method: "POST",
      body: JSON.stringify(all ? { all: true } : { ids }),
    }),

  // ─── Web Push subscriptions (migration 0049) ───────────────────
  listPushSubscriptions: () => request(`/me/push-subscriptions`),
  registerPushSubscription: ({ endpoint, keys, userAgent }) =>
    request(`/me/push-subscriptions`, {
      method: "POST",
      body: JSON.stringify({ endpoint, keys, userAgent }),
    }),
  unregisterPushSubscription: ({ endpoint }) =>
    request(`/me/push-subscriptions/unsubscribe`, {
      method: "POST",
      body: JSON.stringify({ endpoint }),
    }),

  // Re-engagement suggestions — ranked list of customers to reach out to.
  getReEngagementSuggestions: () => request(`/me/suggestions`),

  // Reverse-lookup by the customer-facing BG code (migration 0038).
  // Code is opaque, 8 chars, no 0/1/I/O. Server returns the full post
  // payload; sourceUrl is null for free-tier resellers (premium gate)
  // and populated for premium + staff.
  getPostByCode: (code) => request(`/posts/by-code/${encodeURIComponent(code.trim().toUpperCase())}`),

  // ─── Admin: premium grant (Phase A) ───
  // POST /api/users/:id/premium with { months: N | 0 | null }.
  // months>0 extends premium_until by N months from max(now, current).
  // months 0 or null revokes immediately. Server returns the new
  // { isPremium, premiumUntil } so the caller can update local state
  // without re-fetching the user list.
  adminSetPremium: (userId, months) =>
    request(`/users/${userId}/premium`, {
      method: "POST",
      body: JSON.stringify({ months }),
    }),

  // ─── Subscription orders (migration 0039) ───
  // Customer side: file an in-app order, admin reviews and approves
  // out-of-band payment. Replaces the temporary "DM @Buyergrambot" CTA.
  createSubscriptionOrder: ({ tier, billingPeriod, notes }) =>
    request("/me/subscription-orders", {
      method: "POST",
      body: JSON.stringify({ tier, billingPeriod, notes }),
    }),
  listMySubscriptionOrders: () => request("/me/subscription-orders"),
  cancelMySubscriptionOrder: (orderId) =>
    request(`/me/subscription-orders/${orderId}/cancel`, { method: "POST" }),
  // Admin side: queue + decisions.
  listSubscriptionOrders: (status) =>
    request(`/subscription-orders${status ? `?status=${encodeURIComponent(status)}` : ""}`),
  approveSubscriptionOrder: (orderId, adminNotes) =>
    request(`/subscription-orders/${orderId}/approve`, {
      method: "POST",
      body: JSON.stringify({ adminNotes }),
    }),
  rejectSubscriptionOrder: (orderId, adminNotes) =>
    request(`/subscription-orders/${orderId}/reject`, {
      method: "POST",
      body: JSON.stringify({ adminNotes }),
    }),

  // ─── Image upload (Phase 2b) ───
  // Special: uses multipart/form-data, not JSON. Returns { path, url, width, height, bytes, format }.
  uploadImage: async (file) => {
    const form = new FormData();
    form.append("image", file);
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    // Don't set Content-Type — fetch sets it to multipart/form-data with the right boundary.
    const res = await fetch("/api/upload", { method: "POST", body: form, headers });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Upload failed");
    return data;
  },
  uploadAudio: async (blob, filename = "voice.webm") => {
    const form = new FormData();
    form.append("audio", blob, filename);
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch("/api/upload/audio", { method: "POST", body: form, headers });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Upload failed");
    return data;
  },
};
